/**
 * AudioEngine — per-stage spatial audio.
 *
 * One PositionalAudio per stage, fed by an HTMLAudioElement so we can
 * stream archive.org URLs without CORS pain. The schedule clock advances
 * the playhead and crossfades between scheduled tracks.
 *
 * Audio is unlocked on first user interaction (the spawn picker click).
 */

import * as THREE from 'three';
import type { StageHandle } from '@/world/stages/StageBase';
import type { FullSchedule, ScheduleTrack } from '@shared/schedule';
import { trackAtSimTime } from '@shared/schedule';
import { festivalClock } from '@/core/Time';
import { useUI } from '@/state/store';

interface StageAudio {
  stageId: string;
  positional: THREE.PositionalAudio;
  audioEl: HTMLAudioElement;
  currentTrackKey: string | null;
}

export class AudioEngine {
  readonly listener = new THREE.AudioListener();
  private stages = new Map<string, StageAudio>();
  private schedule: FullSchedule | null = null;
  private masterVolume = 0.85;
  private unlocked = false;

  constructor(camera: THREE.Camera) {
    camera.add(this.listener);
  }

  setSchedule(schedule: FullSchedule): void {
    this.schedule = schedule;
    festivalClock.setEpoch(schedule.epochMs);
  }

  /** Bind a stage handle to the audio engine. Call after world build. */
  registerStage(handle: StageHandle): void {
    const audioEl = document.createElement('audio');
    audioEl.crossOrigin = 'anonymous';
    audioEl.preload = 'auto';
    audioEl.loop = true;
    // Create the PositionalAudio node and route the <audio> element through it.
    const positional = new THREE.PositionalAudio(this.listener);
    try {
      positional.setMediaElementSource(audioEl);
    } catch {
      // ignore — will retry once user interaction unlocks the context
    }
    positional.setRefDistance(handle.audioFalloff.ref);
    positional.setMaxDistance(handle.audioFalloff.max);
    positional.setRolloffFactor(1.6);
    positional.setDistanceModel('exponential');
    positional.setVolume(this.masterVolume);
    handle.audioAnchor.add(positional);

    this.stages.set(handle.id, {
      stageId: handle.id,
      positional,
      audioEl,
      currentTrackKey: null
    });
  }

  /** Called once when the user clicks anything to satisfy autoplay rules. */
  async unlock(): Promise<void> {
    if (this.unlocked) return;
    const ctx = this.listener.context;
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }
    this.unlocked = true;
    // Kick off audio for whichever track each stage should be playing now.
    this.tick();
  }

  setMasterVolume(v: number): void {
    this.masterVolume = v;
    for (const a of this.stages.values()) {
      a.positional.setVolume(v);
    }
  }

  /** Re-evaluate scheduled tracks. Call ~once a second. */
  tick(): void {
    if (!this.schedule || !this.unlocked) return;
    const t = festivalClock.now();
    const nowPlayingForUI: { stageId: string; artist: string; trackTitle: string }[] = [];
    for (const stageSched of this.schedule.stages) {
      const audio = this.stages.get(stageSched.stageId);
      if (!audio) continue;
      const track = trackAtSimTime(stageSched, t.day, t.hour + t.minute / 60);
      const key = `${track.artist}::${track.title}::${track.archiveId ?? track.url ?? ''}`;
      if (key !== audio.currentTrackKey) {
        audio.currentTrackKey = key;
        const url = trackUrl(track);
        if (url) {
          audio.audioEl.src = url;
          audio.audioEl.play().catch(() => {
            // Silent — bad source, broken CORS, or autoplay blocked.
            // The "now playing" UI still shows the artist; we just don't
            // hear the audio. Real verified URLs land in v1.1.
          });
        }
      }
      nowPlayingForUI.push({
        stageId: stageSched.stageId,
        artist: track.artist,
        trackTitle: track.title
      });
    }
    useUI.getState().setNowPlaying(nowPlayingForUI);
  }
}

function trackUrl(track: ScheduleTrack): string | null {
  if (track.url) return track.url;
  if (track.archiveId && track.archiveFile) {
    return `https://archive.org/download/${track.archiveId}/${track.archiveFile}`;
  }
  // Don't auto-construct from archiveId alone — those URLs are not
  // guaranteed to exist and trip CORS preflight noise. Curated URLs only.
  return null;
}
